import { useCallback, useEffect, useRef, useState } from "react";

// Chuỗi chụp: đếm ngược → chụp → đếm ngược → chụp … đủ số tấm thì dừng.
// Chế độ bấm từng tấm chỉ là trường hợp riêng với shots = 1.
//
// Hai điều bắt buộc phải đúng:
//
// 1. Đếm ngược theo MỐC THỜI GIAN (`Date.now`) chứ không đếm số nhịp.
//    Khi tab bị ẩn, trình duyệt bóp `setInterval` còn khoảng 1 lần/giây (đo
//    thực tế trên máy này: nhịp 200 ms mất 666 ms). Đếm theo nhịp thì mỗi tấm
//    chậm một ít, bốn tấm cộng dồn lại lệch rất xa. So với mốc thời gian thì
//    dù có bỏ lỡ bao nhiêu nhịp, tổng thời gian vẫn đúng.
//
// 2. Huỷ phải sạch tuyệt đối. Mỗi lượt chạy mang một số hiệu; huỷ = tăng số
//    hiệu lên, mọi vòng lặp cũ tự nhận ra mình lạc hậu và dừng ngay. Nhờ vậy
//    bấm "Quay lại" hay "Dừng" giữa chừng không để sót thêm tấm nào.
export function useCaptureSequence({ seconds, onShoot, onTick }) {
    const [countdown, setCountdown] = useState(null);
    const [running, setRunning] = useState(false);

    const tickRef = useRef(null);
    const runIdRef = useRef(0);

    // Đọc giá trị mới nhất mà không phải dựng lại `start`, nhờ vậy `start` giữ
    // được tham chiếu ổn định và không làm hỏng chuỗi đang chạy khi cài đặt đổi.
    const secondsRef = useRef(seconds);
    secondsRef.current = seconds;

    const onShootRef = useRef(onShoot);
    onShootRef.current = onShoot;

    // Gọi mỗi khi con số hiển thị ĐỔI (không phải mỗi nhịp) — dùng để kêu bíp.
    const onTickRef = useRef(onTick);
    onTickRef.current = onTick;

    const stop = useCallback(() => {
        runIdRef.current += 1;
        if (tickRef.current) {
            clearInterval(tickRef.current);
            tickRef.current = null;
        }
        setCountdown(null);
        setRunning(false);
    }, []);

    // Rời trang giữa chừng cũng phải dừng.
    useEffect(() => stop, [stop]);

    const countOnce = useCallback(
        runId =>
            new Promise(resolve => {
                const total = secondsRef.current;
                const deadline = Date.now() + total * 1000;

                // Nhịp chạy 200 ms cho mượt, nhưng con số chỉ đổi mỗi giây. Nhớ
                // lại giá trị đang hiện để không gọi setState thừa 4 lần/giây
                // và để tiếng bíp kêu đúng một lần mỗi giây.
                let shown = total;
                setCountdown(total);
                onTickRef.current?.(total);

                tickRef.current = setInterval(() => {
                    if (runId !== runIdRef.current) return; // lượt này đã bị huỷ

                    const left = Math.ceil((deadline - Date.now()) / 1000);
                    if (left > 0) {
                        if (left !== shown) {
                            shown = left;
                            setCountdown(left);
                            onTickRef.current?.(left);
                        }
                        return;
                    }

                    clearInterval(tickRef.current);
                    tickRef.current = null;
                    setCountdown(null);
                    resolve();
                }, 200);
            }),
        []
    );

    // shots = số tấm cần chụp trong lượt này.
    const start = useCallback(
        async shots => {
            if (shots <= 0) return;

            runIdRef.current += 1;
            const runId = runIdRef.current;
            setRunning(true);

            for (let i = 0; i < shots; i++) {
                await countOnce(runId);
                if (runId !== runIdRef.current) return;

                const ok = await onShootRef.current();
                // Kiểm tra lại sau khi chụp: người dùng có thể bấm Dừng đúng lúc
                // ảnh đang được nạp.
                if (runId !== runIdRef.current) return;
                if (!ok) break; // chụp hỏng (mất camera…) thì dừng hẳn
            }

            setRunning(false);
        },
        [countOnce]
    );

    return { countdown, running, start, stop };
}

export default useCaptureSequence;
